import keycloak from "../../keycloak";
import { getTenant } from "../../api/ApiUtils";
import { KeycloakApi } from "../../api/KeycloakApi";
import { LoginDto, TokenDto } from "../models/LoginDto";

interface KeycloakTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
  token_type: string;
}

interface LoyaltyTokenClaims {
  customer_number?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
}

const PUBLIC_TOKEN_REFRESH_BUFFER_SEC = 30;

let publicAccessToken: string | undefined;
let publicAccessTokenExpiresAt = 0;

async function getPublicToken(): Promise<string | undefined> {
  if (publicAccessToken && Date.now() / 1000 < publicAccessTokenExpiresAt - PUBLIC_TOKEN_REFRESH_BUFFER_SEC) {
    return publicAccessToken;
  }
  const tenant = getTenant().toUpperCase();
  const username = window.env[`REACT_APP_${tenant}_PUBLIC_USER`] || process.env[`REACT_APP_${tenant}_PUBLIC_USER`];
  const password =
    window.env[`REACT_APP_${tenant}_PUBLIC_PASSWORD`] || process.env[`REACT_APP_${tenant}_PUBLIC_PASSWORD`];
  if (!username || !password) {
    console.warn("Missing tenant public-user credentials");
    return undefined;
  }
  const raw = await KeycloakApi.login({ username, password } as LoginDto);
  const parsed = JSON.parse(raw) as KeycloakTokenResponse;
  publicAccessToken = parsed.access_token;
  publicAccessTokenExpiresAt = Date.now() / 1000 + parsed.expires_in;
  return publicAccessToken;
}

export async function ensureToken(useSelfToken: boolean = true): Promise<TokenDto | undefined> {
  if (useSelfToken) {
    if (!keycloak.authenticated || !keycloak.token) {
      keycloak.login();
      return undefined;
    }
    const claims = (keycloak.tokenParsed ?? {}) as LoyaltyTokenClaims;
    return {
      access_token: keycloak.token,
      customer_number: claims.customer_number ?? "",
    };
  }
  const access_token = await getPublicToken();
  if (!access_token) return undefined;
  return { access_token, customer_number: "" };
}

export function logout() {
  publicAccessToken = undefined;
  publicAccessTokenExpiresAt = 0;
  keycloak.logout();
}

export function isLoggedIn(): boolean {
  return !!keycloak.authenticated;
}

export async function tokenRequestOptions(method: string, useSelfToken: boolean = true): Promise<RequestInit | undefined> {
  const token = await ensureToken(useSelfToken);
  if (!token) return undefined;
  return {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token.access_token,
    },
  };
}

export async function tokenRequestOptionsForMultipart(
  method: string,
  useSelfToken: boolean = true
): Promise<RequestInit | undefined> {
  const token = await ensureToken(useSelfToken);
  if (!token) return undefined;
  return {
    method,
    headers: {
      Authorization: "Bearer " + token.access_token,
    },
  };
}

export async function parseResponse(response: Response) {
  if (response.status === 401) {
    keycloak.login();
    return;
  }
  const text = await response.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    if (response.status !== 200 && response.status !== 201 && response.status !== 204) throw response.statusText;
  }
  if (data && data.error) {
    throw data.error;
  }
  return data;
}
